/**
 * ProjectWorkspace — FWM projects, their owners and their milestones.
 *
 * The work that spans modules: a hospital build, a college term, a conference.
 * Owners are picked from the people FWM already holds, so nobody gains a
 * second identity by being given a task, and a project only ever references
 * the records that own the underlying thing.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  FolderKanban, Plus, Loader2, Flag, CalendarClock, MessageSquare, Users, Link2,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const whenSaid = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short',
                                       hour: '2-digit', minute: '2-digit' });
};

const DateField = ({ value, onChange, placeholder = 'Pick a date', testId,
                    className = '' }) => {
  const picked = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={testId}
                className={`justify-start font-normal ${value ? '' : 'text-slate-400'} ${className}`}>
          <CalendarClock className="mr-2 h-3.5 w-3.5" />
          {picked ? picked.toLocaleDateString(undefined,
            { day: 'numeric', month: 'short', year: 'numeric' }) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={picked} initialFocus
                  onSelect={(d) => onChange(d ? d.toISOString().slice(0, 10) : '')} />
      </PopoverContent>
    </Popover>
  );
};

const STATUS_STYLE = {
  planning: 'bg-slate-100 text-slate-700',
  active: 'bg-emerald-100 text-emerald-800',
  on_hold: 'bg-amber-100 text-amber-900',
  done: 'bg-indigo-100 text-indigo-800',
  cancelled: 'bg-rose-100 text-rose-800',
};

const PersonPicker = ({ value, onChange, testId }) => {
  const [term, setTerm] = useState('');
  const { data } = useQuery({
    queryKey: ['project-people', term],
    queryFn: () => api.get(`/projects/people/search?q=${encodeURIComponent(term)}`)
      .then((r) => r.data),
    enabled: term.trim().length >= 2,
  });
  return (
    <div>
      <Input placeholder="Search people already on FWM…" value={term}
             onChange={(e) => setTerm(e.target.value)} data-testid={testId} />
      {value ? (
        <p className="mt-1 text-[11px] text-emerald-700">Selected: {value.name}</p>
      ) : null}
      {(data?.items || []).length > 0 && (
        <div className="mt-1 max-h-36 overflow-auto rounded border bg-white">
          {data.items.map((p) => (
            <button key={p.key} type="button"
                    className="block w-full px-2 py-1 text-left text-xs hover:bg-slate-50"
                    onClick={() => { onChange(p); setTerm(''); }}
                    data-testid={`${testId}-option`}>
              {p.name}
              <span className="text-slate-400"> · {(p.modules || []).join(', ')}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProjectWorkspace = () => {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ scope_type: '', status: '', q: '' });
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    name: '', description: '', scope_type: 'assembly', scope_id: '',
    status: 'planning', due_date: '', manager: null,
  });
  const [task, setTask] = useState({ title: '', due_date: '', assignee: null });
  const [milestone, setMilestone] = useState({ title: '', due_date: '' });
  const [note, setNote] = useState('');

  const { data: meta } = useQuery({
    queryKey: ['projects-meta'],
    queryFn: () => api.get('/projects/meta').then((r) => r.data),
  });
  const query = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v)).toString();
  const { data, isLoading } = useQuery({
    queryKey: ['projects', query],
    queryFn: () => api.get(`/projects${query ? `?${query}` : ''}`).then((r) => r.data),
  });
  const { data: scopeOptions } = useQuery({
    queryKey: ['project-scope-options', draft.scope_type],
    enabled: adding && !!draft.scope_type,
    queryFn: () => api.get('/projects/scope-options',
      { params: { scope_type: draft.scope_type } }).then((r) => r.data),
  });
  const { data: open } = useQuery({
    queryKey: ['project', openId],
    queryFn: () => api.get(`/projects/${openId}`).then((r) => r.data),
    enabled: !!openId,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['projects'] });
    if (openId) qc.invalidateQueries({ queryKey: ['project', openId] });
  };
  const fail = (e) => toast.error(e.response?.data?.detail || 'That did not work');

  const create = useMutation({
    mutationFn: () => api.post('/projects', {
      ...draft, manager_key: draft.manager?.key || '',
    }).then((r) => r.data),
    onSuccess: (d) => {
      toast.success('Project created');
      setAdding(false);
      setDraft({ ...draft, name: '', description: '', scope_id: '', manager: null });
      setOpenId(d.id);
      refresh();
    },
    onError: fail,
  });

  const addTask = useMutation({
    mutationFn: () => api.post(`/projects/${openId}/tasks`, {
      title: task.title, due_date: task.due_date,
      assignee_key: task.assignee?.key || '',
    }),
    onSuccess: () => {
      toast.success('Task added');
      setTask({ title: '', due_date: '', assignee: null });
      refresh();
    },
    onError: fail,
  });

  const moveTask = useMutation({
    mutationFn: ({ id, status }) =>
      api.patch(`/projects/${openId}/tasks/${id}`, { status }),
    onSuccess: () => refresh(),
    onError: fail,
  });

  const addMilestone = useMutation({
    mutationFn: () => api.post(`/projects/${openId}/milestones`, milestone),
    onSuccess: () => {
      toast.success('Milestone added');
      setMilestone({ title: '', due_date: '' });
      refresh();
    },
    onError: fail,
  });

  const addComment = useMutation({
    mutationFn: () => api.post(`/projects/${openId}/comments`, { body: note }),
    onSuccess: () => { setNote(''); refresh(); },
    onError: fail,
  });

  const scopes = meta?.scopes || [];
  const creatable = scopes.filter((s) => s.can_create);

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6" data-testid="project-workspace">
      <a href="/admin" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600"
         data-testid="projects-back">
        <ArrowLeft className="h-3 w-3" /> Back to the admin workspace
      </a>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-serif text-2xl text-slate-900">
            <FolderKanban className="h-6 w-6 text-indigo-600" /> Projects & Teams
          </h1>
          <p className="mt-1 text-sm text-slate-500" data-testid="projects-scope-note">
            {!meta ? 'Loading your view…' : meta.central_view
              ? 'Organisation-wide view — every project across FWM.'
              : 'Your assemblies, your institutions, and the work assigned to you.'}
          </p>
        </div>
        {creatable.length > 0 && (
          <Button onClick={() => setAdding(true)} data-testid="projects-new">
            <Plus className="mr-1 h-4 w-4" /> New project
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Input placeholder="Search projects…" value={filters.q} className="max-w-xs"
               onChange={(e) => setFilters({ ...filters, q: e.target.value })}
               data-testid="projects-search" />
        <Select value={filters.status || 'all'}
                onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-40" data-testid="projects-filter-status">
            <SelectValue placeholder="Any status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            {(meta?.statuses || []).map((s) => (
              <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.scope_type || 'all'}
                onValueChange={(v) => setFilters({ ...filters, scope_type: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-44" data-testid="projects-filter-scope">
            <SelectValue placeholder="Anywhere" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Anywhere</SelectItem>
            {scopes.map((s) => <SelectItem key={s.key} value={s.key}>{s.key}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <Loader2 className="mx-auto my-10 h-5 w-5 animate-spin" />
      ) : (data?.items || []).length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-slate-500"
                           data-testid="projects-empty">
          No projects yet. Create one for a build, a term's preparation or a conference.
        </CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="projects-list">
          {data.items.map((p) => (
            <button key={p.id} type="button" onClick={() => setOpenId(p.id)}
                    className="rounded-lg border bg-white p-4 text-left transition-colors hover:border-indigo-300"
                    data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-slate-900">{p.name}</p>
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${STATUS_STYLE[p.status] || ''}`}>
                  {p.status.replace('_', ' ')}
                </span>
              </div>
              <p className="mt-1 text-[11px] text-slate-500">
                {p.scope?.type}{p.scope?.name ? ` · ${p.scope.name}` : ''}
                {p.due_date ? ` · due ${p.due_date}` : ''}
              </p>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-indigo-500"
                     style={{ width: `${p.progress?.percent || 0}%` }} />
              </div>
              <p className="mt-1 text-[11px] text-slate-400">
                {p.progress?.done || 0}/{p.progress?.tasks || 0} tasks
                {p.progress?.overdue ? ` · ${p.progress.overdue} overdue` : ''}
                {p.manager?.name ? ` · ${p.manager.name}` : ''}
              </p>
            </button>
          ))}
        </div>
      )}

      {/* ── project detail ── */}
      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto"
                       data-testid="project-detail">
          <DialogHeader>
            <DialogTitle>{open?.name || 'Project'}</DialogTitle>
          </DialogHeader>
          {!open ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : (
            <div className="space-y-5">
              <p className="text-sm text-slate-600">{open.description}</p>
              <div className="flex flex-wrap gap-2 text-[11px]">
                <Badge variant="outline">{open.scope?.type} {open.scope?.name}</Badge>
                <Badge variant="outline">{open.status}</Badge>
                {open.due_date && <Badge variant="outline">due {open.due_date}</Badge>}
                {open.manager?.name && (
                  <Badge variant="outline"><Users className="mr-1 h-3 w-3" />{open.manager.name}</Badge>
                )}
                {(open.links || []).map((l, i) => (
                  <Badge key={i} variant="outline" data-testid={`project-link-${l.kind}`}>
                    <Link2 className="mr-1 h-3 w-3" />{l.label || l.kind}
                  </Badge>
                ))}
              </div>

              <section>
                <h3 className="mb-2 flex items-center gap-1 text-sm font-semibold">
                  <Flag className="h-4 w-4 text-indigo-600" /> Milestones
                </h3>
                {(open.milestones || []).length === 0 ? (
                  <p className="text-xs text-slate-400">None yet.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {open.milestones.map((m) => (
                      <li key={m.id} className="flex items-center gap-2"
                          data-testid={`project-milestone-${m.id}`}>
                        <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
                        {m.title}
                        <span className="text-[11px] text-slate-400">{m.due_date}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-2 flex gap-2">
                  <Input placeholder="Milestone" value={milestone.title} className="h-8"
                         onChange={(e) => setMilestone({ ...milestone, title: e.target.value })}
                         data-testid="project-milestone-title" />
                  <Input type="date" value={milestone.due_date} className="h-8 w-40"
                         onChange={(e) => setMilestone({ ...milestone, due_date: e.target.value })}
                         data-testid="project-milestone-date" />
                  <Button size="sm" className="h-8" disabled={!milestone.title.trim()}
                          onClick={() => addMilestone.mutate()}
                          data-testid="project-milestone-add">Add</Button>
                </div>
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold">Tasks</h3>
                <div className="divide-y divide-slate-100 rounded border">
                  {(open.tasks || []).length === 0 ? (
                    <p className="p-3 text-xs text-slate-400">No tasks yet.</p>
                  ) : open.tasks.map((t) => (
                    <div key={t.id} className="flex flex-wrap items-center gap-2 p-2"
                         data-testid={`project-task-${t.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-slate-900">{t.title}</p>
                        <p className="text-[11px] text-slate-400">
                          {t.assignee?.name || 'unassigned'}{t.due_date ? ` · due ${t.due_date}` : ''}
                        </p>
                      </div>
                      <Select value={t.status}
                              onValueChange={(v) => moveTask.mutate({ id: t.id, status: v })}>
                        <SelectTrigger className="h-7 w-32 text-[11px]"
                                       data-testid={`project-task-status-${t.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(meta?.task_statuses || []).map((s) => (
                            <SelectItem key={s} value={s} className="text-[11px]"
                                        data-testid={`project-task-status-${t.id}-${s}`}>
                              {s.replace('_', ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <div className="mt-2 space-y-2">
                  <div className="flex gap-2">
                    <Input placeholder="New task" value={task.title} className="h-8"
                           onChange={(e) => setTask({ ...task, title: e.target.value })}
                           data-testid="project-task-title" />
                    <Input type="date" value={task.due_date} className="h-8 w-40"
                           onChange={(e) => setTask({ ...task, due_date: e.target.value })}
                           data-testid="project-task-date" />
                    <Button size="sm" className="h-8" disabled={!task.title.trim() || addTask.isPending}
                            onClick={() => addTask.mutate()}
                            data-testid="project-task-add">Add</Button>
                  </div>
                  <PersonPicker value={task.assignee}
                                onChange={(p) => setTask({ ...task, assignee: p })}
                                testId="project-task-assignee" />
                </div>
              </section>

              <section>
                <h3 className="mb-2 flex items-center gap-1 text-sm font-semibold">
                  <MessageSquare className="h-4 w-4 text-indigo-600" /> Comments
                </h3>
                <div className="space-y-2">
                  {(open.comments || []).map((c) => (
                    <div key={c.id} className="rounded bg-slate-50 p-2 text-sm"
                         data-testid={`project-comment-${c.id}`}>
                      <p className="text-slate-700">{c.body}</p>
                      <p className="text-[10px] text-slate-400">{c.author} · {whenSaid(c.created_at)}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <Textarea rows={2} placeholder="Leave a note for the team" value={note}
                            onChange={(e) => setNote(e.target.value)}
                            data-testid="project-comment-body" />
                  <Button size="sm" disabled={!note.trim()} onClick={() => addComment.mutate()}
                          data-testid="project-comment-add">Post</Button>
                </div>
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── new project ── */}
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-lg" data-testid="project-new-dialog">
          <DialogHeader><DialogTitle>New project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={draft.name} data-testid="project-new-name"
                     onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div>
              <Label>What it covers</Label>
              <Textarea rows={3} value={draft.description} data-testid="project-new-description"
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Belongs to</Label>
                <Select value={draft.scope_type}
                        onValueChange={(v) => setDraft({ ...draft, scope_type: v })}>
                  <SelectTrigger data-testid="project-new-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {creatable.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.key}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due</Label>
                <Input type="date" value={draft.due_date} data-testid="project-new-due"
                       onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Which one</Label>
              <Select value={draft.scope_id}
                      onValueChange={(v) => setDraft({ ...draft, scope_id: v })}>
                <SelectTrigger data-testid="project-new-scope-id">
                  <SelectValue placeholder={`Choose the ${draft.scope_type}`} />
                </SelectTrigger>
                <SelectContent>
                  {(scopeOptions?.items || []).map((o) => (
                    <SelectItem key={o.id} value={o.id} data-testid={`project-scope-opt-${o.id}`}>
                      {o.name}{o.where ? ` · ${o.where}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(scopeOptions?.items || []).length === 0 ? (
                <p className="mt-1 text-[11px] text-amber-600" data-testid="project-no-scopes">
                  Nothing of that kind is on the register yet.
                </p>
              ) : null}
            </div>
            <div>
              <Label>Project manager</Label>
              <PersonPicker value={draft.manager}
                            onChange={(p) => setDraft({ ...draft, manager: p })}
                            testId="project-new-manager" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button disabled={!draft.name.trim() || create.isPending}
                    onClick={() => create.mutate()} data-testid="project-new-save">
              {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectWorkspace;
